import type { AaiRequest } from '../../types/aai';

/*
 * The one vocabulary for a client's assigned work.
 *
 * The words come from the server (request_status.py), on every request row as
 * `display`, so a row and the drawer it opens read the same. The tables below
 * are only the fallback for a row that arrived without them.
 */

/** Everything still live. A declined request is closed, not work in hand. */
export const openRequests = (requests: AaiRequest[]): AaiRequest[] =>
  requests.filter((r) => r.status !== 'declined');

/** Request status -> the chip vocabulary the portal shows people. */
export const STATE: Record<string, string> = {
  pending: 'pending',
  needs_info: 'needs_review',
  dept_approved: 'in_progress',
  approved: 'in_delivery',
  declined: 'declined',
};

/** What happens next, said in terms of who holds it rather than of a status name. */
export const NEXT: Record<string, string> = {
  pending: 'Department head review',
  needs_info: 'Client adds the detail asked for',
  dept_approved: 'AI champion review',
  approved: 'Delivery in progress',
  declined: 'Nothing further',
};

/** An owner-scoped Orbit problem: what happens next follows the card's stage. */
const NEXT_BY_STAGE: Record<string, string> = {
  'Scoping with Orbit': 'Client finishes scoping with Orbit',
  Scoped: 'Orbit drafts the PRD',
  Routed: 'Orbit drafts the PRD',
  'PRD ready': 'Client opens the workspace to build',
  Building: 'Delivery in progress',
  Built: 'Publish the delivered tool to the requester',
  Tested: 'Publish the delivered tool to the requester',
};

export const nextFor = (request: AaiRequest): string => {
  if (request.display) return request.display.next;
  if (request.source === 'orbit_problem' && request.status === 'pending') {
    return 'Client owner confirms or declines';
  }
  if (request.problem_stage && request.status === 'approved') {
    return NEXT_BY_STAGE[request.problem_stage] || request.problem_stage;
  }
  return NEXT[request.status] || 'With the program team';
};

export const stateFor = (request: AaiRequest): string => {
  if (request.display) return request.display.chip;
  return STATE[request.status] || request.status;
};

export const ownerOf = (request: AaiRequest): string =>
  request.assigned_expert_email || request.owner_email || request.decided_by || 'Not yet named';
